import {Component, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, DatePipe, NgForOf, NgIf} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {RouterLink} from "@angular/router";
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subscription} from "rxjs";
import {catchError, map, switchMap, take, tap} from "rxjs/operators";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from "@angular/material/select";
import {MatInputModule} from "@angular/material/input";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {provideNativeDateAdapter} from "@angular/material/core";
import {Timestamp} from "firebase/firestore";
import {Game, ScheduleGame} from "../model/broadcast/game";
import {League} from "../model/league";
import {GamesService} from "../services/games.service";
import {LeaguesService} from "../services/leagues.service";
import {SchedulesService} from "../services/schedules.service";

type GameStatus = 'live' | 'final' | 'scheduled' | 'noData';

interface GameRow {
  gameId: string;
  scheduled?: Timestamp;
  homeName: string;
  awayName: string;
  homeRuns?: number;
  awayRuns?: number;
  status: GameStatus;
  hasData: boolean;
}

interface LeagueGames {
  league: League;
  leagueName: string;
  rows: GameRow[];
}

interface LeagueGroup {
  federation: string;
  leagues: League[];
}

@Component({
  selector: 'app-score-sheet-list',
  templateUrl: './score-sheet-list.component.html',
  styleUrls: ['./score-sheet-list.component.scss'],
  standalone: true,
  imports: [NgIf, NgForOf, AsyncPipe, DatePipe, RouterLink, FormsModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatDatepickerModule],
  providers: [provideNativeDateAdapter()],
})
export class ScoreSheetListComponent implements OnInit, OnDestroy {

  groups$: Observable<LeagueGames[]> = of([]);
  leagueGroups$: Observable<LeagueGroup[]> = of([]);
  loading = true;

  selectedDate: Date = this.startOfDay(new Date());
  selectedLeagueId: string = '';

  private dateSubject = new BehaviorSubject<Date>(this.startOfDay(new Date()));
  private leagueSubject = new BehaviorSubject<string>('');
  private subscriptions: Subscription[] = [];
  private scheduleCache = new Map<string, ScheduleGame[]>();

  constructor(
    private gamesService: GamesService,
    private leaguesService: LeaguesService,
    private schedulesService: SchedulesService,
  ) {
  }

  ngOnInit(): void {
    const leagues$ = this.leaguesService.getAllLeagues().pipe(
      map(leagues => leagues.filter(l => this.isLicenced(l))
        .sort((a, b) => a.name.localeCompare(b.name))),
    );
    this.leagueGroups$ = leagues$.pipe(map(leagues => this.buildLeagueGroups(leagues)));

    const games$ = this.dateSubject.pipe(
      switchMap(date => this.gamesService.getGamesForDate(date)),
    );

    const schedules$ = combineLatest([leagues$, this.leagueSubject]).pipe(
      switchMap(([leagues, filter]) => {
        const target = filter ? leagues.filter(l => l.id === filter) : leagues;
        return this.fetchAllSchedules(target);
      }),
    );

    this.groups$ = combineLatest([games$, leagues$, schedules$, this.dateSubject, this.leagueSubject]).pipe(
      map(([games, leagues, schedules, date, leagueId]) =>
        this.buildGroups(games, leagues, schedules, date, leagueId)),
    );

    const sub = this.groups$.subscribe(() => this.loading = false);
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onDateChange(date: Date | null): void {
    if (!date) return;
    this.loading = true;
    this.dateSubject.next(this.startOfDay(date));
  }

  onLeagueChange(): void {
    this.loading = true;
    this.leagueSubject.next(this.selectedLeagueId);
  }

  private fetchAllSchedules(leagues: League[]): Observable<Map<string, ScheduleGame[]>> {
    if (!leagues.length) return of(new Map());
    return forkJoin(
      leagues.map(l => this.getLeagueSchedule(l).pipe(map(games => [l.id, games] as const))),
    ).pipe(map(entries => new Map(entries)));
  }

  private getLeagueSchedule(league: League): Observable<ScheduleGame[]> {
    if (this.scheduleCache.has(league.id)) {
      return of(this.scheduleCache.get(league.id));
    }
    if (!league.url) {
      this.scheduleCache.set(league.id, []);
      return of([]);
    }
    return this.schedulesService.getSchedule(league.id).pipe(
      take(1),
      catchError(() => of([] as ScheduleGame[])),
      tap(games => this.scheduleCache.set(league.id, games ?? [])),
    );
  }

  private buildLeagueGroups(leagues: League[]): LeagueGroup[] {
    const map = new Map<string, League[]>();
    for (const l of leagues) {
      const fed = l.federation?.trim() || 'Other';
      if (!map.has(fed)) map.set(fed, []);
      map.get(fed).push(l);
    }
    return Array.from(map.entries())
      .map(([federation, ls]) => ({
        federation,
        leagues: ls.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.federation.localeCompare(b.federation));
  }

  private buildGroups(
    games: Game[],
    leagues: League[],
    schedules: Map<string, ScheduleGame[]>,
    date: Date,
    leagueFilter: string,
  ): LeagueGames[] {
    const dayStart = this.startOfDay(date).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;
    const now = Date.now();

    const leagueById = new Map<string, League>(leagues.map(l => [l.id, l]));
    const groups = new Map<string, LeagueGames>();

    const ensureGroup = (league: League): LeagueGames => {
      let g = groups.get(league.id);
      if (!g) {
        g = {league, leagueName: league.name ?? '', rows: []};
        groups.set(league.id, g);
      }
      return g;
    };

    const handled = new Set<string>();

    for (const league of leagues) {
      if (leagueFilter && leagueFilter !== league.id) continue;
      const schedule = schedules.get(league.id) ?? [];
      for (const sg of schedule) {
        if (!sg.gameId) continue;
        const ts = this.toMillis(sg.scheduled);
        if (!ts || ts < dayStart || ts >= dayEnd) continue;
        if (!this.scheduleGameIsLicenced(league, sg)) continue;

        const sgKey = String(sg.gameId);
        const fsGame = games.find(g => String(g.gameId) === sgKey && g.tournamentId === league.id);
        const row = fsGame
          ? this.rowFromGame(fsGame, sg)
          : this.rowFromSchedule(sg, now);
        ensureGroup(league).rows.push(row);
        handled.add(`${league.id}::${sgKey}`);
      }
    }

    for (const game of games) {
      if (!game.tournamentId) continue;
      const league = leagueById.get(game.tournamentId);
      if (!league) continue;
      if (leagueFilter && leagueFilter !== league.id) continue;
      if (!this.gameIsLicenced(league, game)) continue;
      const key = `${league.id}::${String(game.gameId)}`;
      if (handled.has(key)) continue;
      ensureGroup(league).rows.push(this.rowFromGame(game, undefined));
    }

    return Array.from(groups.values())
      .map(g => ({
        ...g,
        rows: g.rows.sort((a, b) => this.toMillis(a.scheduled) - this.toMillis(b.scheduled)),
      }))
      .sort((a, b) => a.leagueName.localeCompare(b.leagueName));
  }

  private rowFromGame(game: Game, schedule: ScheduleGame | undefined): GameRow {
    const scheduled = game.scheduled ?? schedule?.scheduled;
    const status: GameStatus = game.finished ? 'final' : 'live';
    return {
      gameId: game.gameId,
      scheduled,
      homeName: game.home?.fullName || game.home?.shortName || schedule?.home?.fullName || schedule?.home?.shortName || 'Home',
      awayName: game.away?.fullName || game.away?.shortName || schedule?.away?.fullName || schedule?.away?.shortName || 'Away',
      homeRuns: game.home?.runs,
      awayRuns: game.away?.runs,
      status,
      hasData: true,
    };
  }

  private rowFromSchedule(sg: ScheduleGame, now: number): GameRow {
    const status: GameStatus = sg.finished
      ? 'final'
      : (this.toMillis(sg.scheduled) <= now ? 'noData' : 'scheduled');
    return {
      gameId: sg.gameId,
      scheduled: sg.scheduled,
      homeName: sg.home?.fullName || sg.home?.shortName || 'Home',
      awayName: sg.away?.fullName || sg.away?.shortName || 'Away',
      status,
      hasData: false,
    };
  }

  private isLicenced(league: League): boolean {
    if (!league) return false;
    if (league.licenced) return true;
    return !!league.teams?.some(t => t.licenced);
  }

  private gameIsLicenced(league: League, game: Game): boolean {
    if (league.licenced) return true;
    const homeId = game.home?.guid;
    if (homeId && league.teams?.find(t => t.id === homeId)?.licenced) return true;
    const awayId = game.away?.guid;
    if (awayId && league.teams?.find(t => t.id === awayId)?.licenced) return true;
    return false;
  }

  private scheduleGameIsLicenced(league: League, sg: ScheduleGame): boolean {
    if (league.licenced) return true;
    const homeId = sg.home?.id;
    if (homeId && league.teams?.find(t => t.id === homeId)?.licenced) return true;
    const awayId = sg.away?.id;
    if (awayId && league.teams?.find(t => t.id === awayId)?.licenced) return true;
    return false;
  }

  private toMillis(ts: any): number {
    if (!ts) return 0;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts.seconds === 'number') return ts.seconds * 1000;
    return 0;
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  statusLabel(status: GameStatus): string {
    switch (status) {
      case 'live': return 'Live';
      case 'final': return 'Finished';
      case 'scheduled': return 'Scheduled';
      case 'noData': return 'No data';
    }
  }
}
